import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input, Label } from '../common';
import { fieldValidators } from '../../utils/validationSchemas';

/**
 * PrimaryAdministratorStep Component
 *
 * Step 2 of the organization creation wizard.
 * Collects primary administrator contact information.
 *
 * Senior Developer Implementation:
 * - Real-time field validation
 * - Clear error messages
 * - Type-safe validation
 * - User-friendly feedback
 * - Reuses validation logic from validationSchemas
 */
export const PrimaryAdministratorStep = ({
  initialData,
  onChange = () => {},
  onPrevious = () => {},
  onSubmit = () => {},
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState(() => ({
    fullName: initialData?.fullName ?? '',
    phoneNumber: initialData?.phoneNumber ?? '',
  }));

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Clear errors on mount to ensure no errors are shown initially
  useEffect(() => {
    setErrors({});
    setTouched({});
  }, []);

  // Validate field on change (after it's been touched)
  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    const updated = { ...formData, [field]: value };
    setFormData(updated);

    // Validate if field has been touched
    if (touched[field]) {
      validateField(field, value);
    }

    // Notify parent of changes (will be handled by useEffect)
    // Don't call onChange directly here to avoid duplicate calls
  };

  // Validate single field
  const validateField = (field, value) => {
    let error = null;

    if (fieldValidators[field]) {
      error = fieldValidators[field](value);
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  // Handle blur event - mark field as touched and validate
  const handleBlur = (field) => {
    setHasInteracted(true);
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Validate all fields (only called when user tries to submit)
  const validateAll = () => {
    const newTouched = {};
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach((field) => {
      newTouched[field] = true;
      let error = null;

      if (fieldValidators[field]) {
        error = fieldValidators[field](formData[field]);
      }

      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);
    setHasInteracted(true);
    return isValid;
  };

  // Mark component as mounted after first render
  useEffect(() => {
    setIsMounted(true);
    onChange(formData, {
      validateAll,
      isValid: undefined,
      errors: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose validation function to parent (only when formData changes, not on mount)
  useEffect(() => {
    if (!isMounted) return;

    if (hasInteracted) {
      const isValid =
        Object.keys(touched).length > 0 &&
        Object.keys(formData).every((field) => {
          return !fieldValidators[field] || !fieldValidators[field](formData[field]);
        });

      onChange(formData, {
        validateAll,
        isValid: Object.keys(touched).length === 0 ? undefined : isValid,
        errors,
      });
    } else {
      onChange(formData, {
        validateAll,
        isValid: undefined,
        errors: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, hasInteracted, touched, errors, isMounted]);

  return (
    <div className="content-stretch flex flex-col gap-[40px] items-center w-full">
      {/* Form Section */}
      <div className="content-stretch flex flex-col gap-[24px] items-center w-full">
        {/* Title and Fields */}
        <div className="content-stretch flex flex-col gap-[24px] items-start w-full">
          {/* Title */}
          <h2 className="font-['Roboto:Semibold',sans-serif] font-semibold text-[20px] text-black tracking-[0.1px] leading-tight">
            Administrator Contact
          </h2>

          {/* Form Fields */}
          <div className="content-stretch flex flex-col gap-[20px] items-start w-full">
            {/* Row 1: Full Name and Phone Number */}
            <div className="content-stretch flex gap-[33px] items-start w-full">
              <div className="form-group flex-1">
                <Label variant="gray" isRequired={true}>
                  Full Name
                </Label>
                <Input
                  variant="primary"
                  customClass={`!max-w-full ${errors.fullName ? 'border-red-500' : ''}`}
                  placeholder="e.g., Sarah Johnson"
                  value={formData.fullName}
                  onChange={handleInputChange('fullName')}
                  onBlur={() => handleBlur('fullName')}
                />
                <div className="min-h-[1.25rem] mt-1" aria-live="polite">
                  {errors.fullName && touched.fullName && (
                    <p className="text-red-500 text-xs">{errors.fullName}</p>
                  )}
                </div>
              </div>
              <div className="form-group flex-1">
                <Label variant="gray" isRequired={true}>
                  Phone Number
                </Label>
                <Input
                  variant="primary"
                  customClass={`!max-w-full ${errors.phoneNumber ? 'border-red-500' : ''}`}
                  placeholder="e.g., +1 (555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  onBlur={() => handleBlur('phoneNumber')}
                  type="tel"
                />
                <div className="min-h-[1.25rem] mt-1" aria-live="polite">
                  {errors.phoneNumber && touched.phoneNumber && (
                    <p className="text-red-500 text-xs">{errors.phoneNumber}</p>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

PrimaryAdministratorStep.propTypes = {
  initialData: PropTypes.shape({
    fullName: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onPrevious: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
};
